import apiClient from './api-client';

// Interfaces matching the backend
export interface SandboxConsentFilters {
    page?: number;
    pageSize?: number;
    collectionPointId?: number;
    clientId?: number;
    startDate?: Date;
    endDate?: Date;
    search?: string;
    sortBy?: string;
}

export interface SandboxConsentData {
    ConsentHeaderId: number;
    DPId: number;
    CollectionPointId: number;
    CollectionPointIdentifier: string;
    ConsentTimestamp: Date;
    LanguageCode: string;
    CreatedDate: Date;
    IdentifierType: string;
    IdentifierValue: string;
    CollectionPointName: string;
    CollectionPointDescription: string;
    HasGeolocation: number;
    PrivacyPolicyVersion: string;
    YourRightsVersion: string;
    ContactDpoVersion: string;
}

// Comprehensive consent details interface matching the API response
export interface SandboxConsentDetails {
    details: {
        consentHeader: {
            consentHeaderId: number;
            consentTimestamp: string;
            consentSource: string;
            languageCode: string;
            createdDate: string;
        };
        dataPrincipal: {
            dpId: number;
            identifierType: string;
            identifierValue: string;
            vaultId?: number;
            isMinor?: boolean;
        };
        client: {
            clientId: number;
            clientName: string;
            clientDomain: string;
        };
    };
    collectionPoint: {
        id: number;
        departmentName: string;
        name: string;
        identifier: string;
        description: string;
        type: string;
        identifierDataElement: {
            name: string;
            type: string;
        };
        trackGeolocation: boolean;
        minorEnabled: boolean;
        parentIdentifier?: {
            name: string;
            type: string;
        };
    };
    geolocation: {
        latitude: number | null;
        longitude: number | null;
        accuracy: number | null;
        geolocationSource: string | null;
        geolocationTimestamp: string | null;
        countryCode: string | null;
        region: string | null;
        city: string | null;
        ipAddress: string | null;
    };
    privacyPolicy: {
        json: any;
        version: string | null;
        languageCode: string | null;
        effectiveDate: string | null;
        lastUpdated: string | null;
    };
    yourRights: {
        json: any;
        version: string | null;
        languageCode: string | null;
        effectiveDate: string | null;
        lastUpdated: string | null;
    };
    contactDpo: {
        json: any;
        version: string | null;
        languageCode: string | null;
        effectiveDate: string | null;
        lastUpdated: string | null;
    };
    dataElements: Array<{
        id: number;
        key: string;
        name: string;
        description: string;
        type: string[];
        isRequired: boolean;
        value: any;
        storageMode: string;
        isPersonalData: boolean;
    }>;
    purposes: Array<{
        id: number;
        key: string;
        name: string;
        description: string;
        consented: boolean;
        isRequired: boolean;
        lawfulGround: {
            id: number;
            code: string;
            label: string;
            description: string;
        };
        purposeCategory: {
            id: number;
            code: string;
            label: string;
            description: string;
        };
        retention: {
            type: {
                id: number;
                code: string;
                label: string;
            };
            timeBasedPeriod: number | null;
            timeBasedUnit: {
                id: number;
                code: string;
                label: string;
            } | null;
            startDateType: {
                id: number;
                code: string;
                label: string;
            };
            customStartDate: string | null;
            extensionAllowed: boolean;
            extensionReason: string | null;
            endConditions: {
                onPurposeFulfilled: boolean;
                onConsentWithdrawn: boolean;
                onRetentionExpired: boolean;
                onErasureRequested: boolean;
                onAccountClosure: boolean;
            };
            legalRetentionRequired: boolean;
            legalRetentionPeriod: number | null;
            expirationDate: string | null;
        };
        preferences: Array<{
            id: number;
            key: string;
            name: string;
            description: string;
            selectedOptions: Array<{
                id: number;
                key: string;
                label: string;
                value: string;
            }>;
            preferenceValue: string;
        }>;
    }>;

    // Collection Point Level Preferences
    collectionPointPreferences: Array<{
        id: number;
        key: string;
        name: string;
        description: string;
        type: string;
        isRequired: boolean;
        displayOrder: number;
        allowMultipleSelection: boolean;
        options: Array<{
            id: number;
            key: string;
            name: string;
            description: string;
            displayOrder: number;
            isDefaultSelected: boolean;
        }>;
        selectedOptions: Array<{
            id: number;
            key: string;
            label: string;
            value: string;
        }>;
        preferenceValue: string;
    }>;
}

export interface SandboxConsentDetailsResponse {
    success: boolean;
    data: SandboxConsentDetails;
}

export interface SandboxConsentListResult {
    data: SandboxConsentData[];
    pagination: {
        currentPage: number;
        pageSize: number;
        totalRecords: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
}

export interface SandboxConsentsQueryParams {
    filters?: Partial<SandboxConsentFilters>;
    pagination?: {
        page: number;
        pageSize: number;
    };
    sortBy?: string;
}

export interface SandboxConsentsResponse {
    success: boolean;
    data: SandboxConsentData[];
    pagination: {
        currentPage: number;
        pageSize: number;
        totalRecords: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
}

class SandboxConsentsService {
    /**
     * Get paginated sandbox consents with filtering
     */
    async getSandboxConsents(params: SandboxConsentsQueryParams): Promise<SandboxConsentsResponse> {
        const queryParams = new URLSearchParams();

        // Add pagination parameters
        if (params.pagination) {
            queryParams.append('page', params.pagination.page.toString());
            queryParams.append('pageSize', params.pagination.pageSize.toString());
        }

        // Add filter parameters
        if (params.filters) {
            if (params.filters.collectionPointId) {
                queryParams.append('collectionPointId', params.filters.collectionPointId.toString());
            }
            if (params.filters.startDate) {
                queryParams.append('startDate', params.filters.startDate.toISOString());
            }
            if (params.filters.endDate) {
                queryParams.append('endDate', params.filters.endDate.toISOString());
            }
            if (params.filters.search) {
                queryParams.append('search', params.filters.search);
            }
            if (params.filters.sortBy) {
                queryParams.append('sortBy', params.filters.sortBy);
            }
        }

        // Add sort parameter if provided at query level
        if (params.sortBy) {
            queryParams.append('sortBy', params.sortBy);
        }

        const response = await apiClient.get<SandboxConsentsResponse>(`/consents/sandbox?${queryParams.toString()}`);
        return response;
    }

    /**
     * Get sandbox consent by ID with comprehensive details
     */
    async getSandboxConsentById(consentId: number): Promise<SandboxConsentDetailsResponse> {
        const response = await apiClient.get<SandboxConsentDetailsResponse>(`/consents/sandbox/${consentId}`);
        return response;
    }
}

export const sandboxConsentsService = new SandboxConsentsService(); 